import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ShieldCheck, Plus, Minus, Save, Loader2,
  Calendar, Hash, Info, AlertTriangle,
  Package, CheckCircle2, RefreshCw, Search, Camera, RotateCcw
} from 'lucide-react';
import { incomingService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { Toast } from '../../../../components';
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal';
import MobileBarcodeScanner from '../../../../components/ERP/MobileBarcodeScanner/MobileBarcodeScanner';
import './IncomingInspection.css';

interface InspectionItem {
  id: number;
  product_id: number;
  product_name?: string;
  sku?: string;
  barcode?: string | null;
  expected_qty: number;
  counted_qty: number;
  lot: string | null;
  expiry: string | null;
  anvisa: string | null;
  is_verified: boolean;
}

interface InspectionData {
  invoiceEntry: {
    id: number;
    number: string;
    supplier_name?: string;
    entry_date: string;
  };
  items: InspectionItem[];
}

interface InspectionDraft {
  savedAt: string;
  items: InspectionItem[];
  scannedItemIds: number[];
  searchTerm: string;
}

const DRAFT_STORAGE_PREFIX = 'wfmed:incoming-inspection-draft:';

const getDraftStorageKey = (inspectionId: string) =>
  `${DRAFT_STORAGE_PREFIX}${inspectionId}`;

const loadDraftFromStorage = (inspectionId: string): InspectionDraft | null => {
  try {
    const raw = localStorage.getItem(getDraftStorageKey(inspectionId));
    if (!raw) return null;
    return JSON.parse(raw) as InspectionDraft;
  } catch {
    return null;
  }
};

const saveDraftToStorage = (inspectionId: string, draft: InspectionDraft) => {
  try {
    localStorage.setItem(getDraftStorageKey(inspectionId), JSON.stringify(draft));
  } catch (error) {
    console.warn('Não foi possível salvar rascunho local da conferência:', error);
  }
};

const clearDraftFromStorage = (inspectionId: string) => {
  localStorage.removeItem(getDraftStorageKey(inspectionId));
};

const mergeItemsWithDraft = (
  serverItems: InspectionItem[],
  draftItems: InspectionItem[]
): InspectionItem[] => {
  const draftById = new Map(draftItems.map(item => [item.id, item]));

  return serverItems.map(serverItem => {
    const draftItem = draftById.get(serverItem.id);
    if (!draftItem) return serverItem;

    return {
      ...serverItem,
      counted_qty: draftItem.counted_qty,
      lot: draftItem.lot,
      expiry: draftItem.expiry,
      anvisa: draftItem.anvisa,
      is_verified: draftItem.is_verified,
    };
  });
};

const hasDraftProgress = (draft: InspectionDraft) =>
  draft.items.some(item =>
    Number(item.counted_qty) > 0 ||
    Boolean(item.lot) ||
    Boolean(item.expiry) ||
    item.is_verified
  );

const IncomingInspection: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [inspectionData, setInspectionData] = useState<InspectionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [finishing, setFinishing] = useState(false);
  const [returningToTriagem, setReturningToTriagem] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState<{
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'danger';
    onConfirm: () => void;
  } | null>(null);
  const [items, setItems] = useState<InspectionItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannedItemIds, setScannedItemIds] = useState<Set<number>>(new Set());
  const [highlightedItemId, setHighlightedItemId] = useState<number | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const localSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const apiSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const updateMobileView = () => {
      setIsMobileView(
        mediaQuery.matches ||
        (navigator.maxTouchPoints > 0 && window.innerWidth <= 1024)
      );
    };

    updateMobileView();
    mediaQuery.addEventListener('change', updateMobileView);
    window.addEventListener('resize', updateMobileView);

    return () => {
      mediaQuery.removeEventListener('change', updateMobileView);
      window.removeEventListener('resize', updateMobileView);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    initialLoadDoneRef.current = false;
    setDraftRestored(false);

    if (id) {
      loadInspectionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const persistDraftLocally = useCallback((
    inspectionId: string,
    draftItems: InspectionItem[],
    draftScannedIds: Set<number>,
    draftSearchTerm: string
  ) => {
    saveDraftToStorage(inspectionId, {
      savedAt: new Date().toISOString(),
      items: draftItems,
      scannedItemIds: Array.from(draftScannedIds),
      searchTerm: draftSearchTerm,
    });
  }, []);

  const persistDraftToApi = useCallback(async (inspectionId: string, draftItems: InspectionItem[]) => {
    await incomingService.updateInspection(parseInt(inspectionId), draftItems);
  }, []);

  const loadInspectionData = async () => {
    try {
      setLoading(true);
      const response = await incomingService.getInspection(parseInt(id!));
      if (response.success) {
        const serverItems: InspectionItem[] = response.data.items || [];
        const draft = loadDraftFromStorage(id!);

        setInspectionData(response.data);

        if (draft?.items?.length) {
          setItems(mergeItemsWithDraft(serverItems, draft.items));
          setScannedItemIds(new Set(draft.scannedItemIds || []));
          setSearchTerm(draft.searchTerm || '');
          setDraftRestored(hasDraftProgress(draft));
        } else {
          setItems(serverItems);
          setScannedItemIds(new Set());
          setSearchTerm('');
        }
      } else {
        showToast('Erro ao carregar dados de inspeção', 'error');
        navigate('/erp/stock/incoming');
      }
    } catch (error: any) {
      console.error('Erro ao carregar dados de inspeção:', error);
      showToast('Erro ao carregar dados de inspeção', 'error');
      navigate('/erp/stock/incoming');
    } finally {
      setLoading(false);
      initialLoadDoneRef.current = true;
    }
  };

  useEffect(() => {
    if (!id || loading || !initialLoadDoneRef.current) return;

    if (localSaveTimeoutRef.current) {
      clearTimeout(localSaveTimeoutRef.current);
    }

    localSaveTimeoutRef.current = setTimeout(() => {
      persistDraftLocally(id, items, scannedItemIds, searchTerm);
    }, 400);

    if (apiSaveTimeoutRef.current) {
      clearTimeout(apiSaveTimeoutRef.current);
    }

    apiSaveTimeoutRef.current = setTimeout(() => {
      persistDraftToApi(id, items).catch((error) => {
        console.warn('Falha ao salvar conferência no servidor (rascunho local mantido):', error);
      });
    }, 2000);

    return () => {
      if (localSaveTimeoutRef.current) {
        clearTimeout(localSaveTimeoutRef.current);
      }
      if (apiSaveTimeoutRef.current) {
        clearTimeout(apiSaveTimeoutRef.current);
      }
    };
  }, [id, items, scannedItemIds, searchTerm, loading, persistDraftLocally, persistDraftToApi]);

  useEffect(() => {
    if (!id || !items.length) return;

    const flushDraftOnExit = () => {
      persistDraftLocally(id, items, scannedItemIds, searchTerm);
    };

    window.addEventListener('beforeunload', flushDraftOnExit);
    return () => window.removeEventListener('beforeunload', flushDraftOnExit);
  }, [id, items, scannedItemIds, searchTerm, persistDraftLocally]);

  const handleItemChange = (itemId: number, field: keyof InspectionItem, value: any) => {
    setItems(prevItems => {
      const newItems = prevItems.map(item =>
        item.id === itemId
          ? { ...item, [field]: value }
          : item
      );
      
      // Verificação automática: marcar como verificado se campos obrigatórios preenchidos
      const updatedItem = newItems.find(item => item.id === itemId);
      if (updatedItem && (field === 'counted_qty' || field === 'lot' || field === 'expiry')) {
        const countedQty = Number(updatedItem.counted_qty) || 0;
        const lot = updatedItem.lot || '';
        const expiry = updatedItem.expiry || '';
        
        if (countedQty > 0 && lot.length > 3 && expiry.length > 5) {
          updatedItem.is_verified = true;
        } else {
          updatedItem.is_verified = false;
        }
      }
      
      return newItems;
    });
  };


  const handleFinish = async () => {
    // Validar campos obrigatórios
    const hasErrors = items.some(item => {
      const countedQty = Number(item.counted_qty) || 0;
      if (countedQty <= 0) return true;
      // Outras validações podem ser adicionadas aqui
      return false;
    });

    if (hasErrors) {
      showToast('Por favor, preencha todos os campos obrigatórios', 'error');
      return;
    }

    try {
      setFinishing(true);
      await incomingService.updateInspection(parseInt(id!), items);
      const response = await incomingService.verify(parseInt(id!));
      if (response.success) {
        clearDraftFromStorage(id!);
        showToast('Inspeção finalizada! A nota está aguardando lançamento no estoque.', 'success');
        // Sempre redirecionar para o dashboard após finalizar inspeção
        navigate('/erp/stock/incoming');
      } else {
        showToast(response.message || 'Erro ao finalizar inspeção', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao finalizar inspeção:', error);
      showToast('Erro ao finalizar inspeção', 'error');
    } finally {
      setFinishing(false);
    }
  };

  const handleBackToTriagem = () => {
    if (!id || !inspectionData) return;

    setConfirmModalConfig({
      title: 'Voltar para Triagem',
      message:
        `Deseja retornar a nota #${inspectionData.invoiceEntry.number} para a triagem (validação XML)? ` +
        'Você poderá corrigir vínculos/quantidades do XML. Depois de aprovar novamente, a nota volta para conferência.',
      type: 'warning',
      onConfirm: async () => {
        setReturningToTriagem(true);
        setShowConfirmModal(false);
        setConfirmModalConfig(null);
        try {
          const response = await incomingService.backToTriagem(parseInt(id, 10), false);
          if (response.success) {
            clearDraftFromStorage(id);
            showToast('Nota retornada para triagem com sucesso!', 'success');
            navigate(`/erp/stock/incoming/validation/${id}`);
          } else {
            showToast(response.message || 'Erro ao voltar para triagem', 'error');
          }
        } catch (error: any) {
          console.error('Erro ao voltar para triagem:', error);
          showToast(
            error.response?.data?.message || 'Erro ao voltar para triagem',
            'error'
          );
        } finally {
          setReturningToTriagem(false);
        }
      },
    });
    setShowConfirmModal(true);
  };

  const findItemByCode = (code: string) => {
    const normalized = code.trim().toLowerCase();
    if (!normalized) return null;

    return items.find(item =>
      item.barcode?.toLowerCase() === normalized ||
      item.sku?.toLowerCase() === normalized ||
      String(item.product_id) === normalized
    ) || null;
  };

  const handleBarcodeScan = (code: string) => {
    const item = findItemByCode(code);

    if (!item) {
      showToast(`Produto não encontrado nesta conferência: ${code}`, 'warning');
      setSearchTerm(code);
      return;
    }

    setSearchTerm('');
    setScannedItemIds(prev => new Set(prev).add(item.id));
    setHighlightedItemId(item.id);
    showToast(`Produto localizado: ${item.product_name || item.sku || code}`, 'success');

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    window.setTimeout(() => {
      document.getElementById(`inspection-item-card-${item.id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 100);

    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedItemId(null);
    }, 2500);
  };

  const filteredItems = items.filter(item => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      item.product_name?.toLowerCase().includes(search) ||
      item.sku?.toLowerCase().includes(search) ||
      item.barcode?.toLowerCase().includes(search) ||
      item.product_id.toString().includes(search)
    );
  });

  const getProgress = () => {
    if (!items.length) return 0;
    const verified = items.filter(item => item.is_verified).length;
    return Math.round((verified / items.length) * 100);
  };

  const formatExpiryForDisplay = (expiry: string | null) => {
    if (!expiry) return '';
    // Se está no formato YYYY-MM-DD, converter para MM/AAAA
    if (expiry.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month] = expiry.split('-');
      return `${month}/${year}`;
    }
    return expiry;
  };

  if (loading) {
    return (
      <div className="incoming-inspection-loading">
        <Loader2 size={32} className="incoming-inspection-spinner" />
        <p>Carregando dados de inspeção...</p>
      </div>
    );
  }

  if (!inspectionData) {
    return null;
  }

  return (
    <div className="incoming-inspection">
      {/* Tool Header */}
      <div className="incoming-inspection-tool-header">
        <div className="incoming-inspection-tool-header-left">
          <button 
            onClick={() => navigate('/erp/stock/incoming')} 
            className="incoming-inspection-tool-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="incoming-inspection-tool-title-row">
              <h1 className="incoming-inspection-tool-title">
                Conferência {id}
              </h1>
              <span className="incoming-inspection-blind-count-badge">
                Contagem Cega Ativa
              </span>
            </div>
            <p className="incoming-inspection-tool-subtitle">
              Auditando: {inspectionData.invoiceEntry.supplier_name || 'Fornecedor'}
            </p>
          </div>
        </div>

        <div className="incoming-inspection-tool-header-right">
          <button
            type="button"
            onClick={handleBackToTriagem}
            disabled={finishing || returningToTriagem}
            className="incoming-inspection-tool-back-triagem-btn"
            title="Voltar para triagem / validação XML"
          >
            {returningToTriagem ? (
              <>
                <Loader2 size={16} className="incoming-inspection-spinner" />
                Voltando...
              </>
            ) : (
              <>
                <RotateCcw size={16} />
                Voltar para Triagem
              </>
            )}
          </button>
          <button 
            onClick={handleFinish}
            disabled={finishing || returningToTriagem || getProgress() < 100}
            className="incoming-inspection-tool-finish-btn"
          >
            {finishing ? (
              <>
                <Loader2 size={16} className="incoming-inspection-spinner" /> Finalizando...
              </>
            ) : (
              <>
                <Save size={16} /> Finalizar e Sincronizar
              </>
            )}
          </button>
        </div>
      </div>

      {/* Busca e leitura de código de barras */}
      <div className="incoming-inspection-search">
        <div className="incoming-inspection-search-wrapper">
          <Search size={18} className="incoming-inspection-search-icon" />
          <input
            type="text"
            placeholder="Buscar produto por nome, SKU, código de barras ou ID..."
            className="incoming-inspection-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        {draftRestored && (
          <p className="incoming-inspection-draft-restored-hint">
            Rascunho restaurado — seus dados foram recuperados da última sessão.
          </p>
        )}
        {scannedItemIds.size > 0 && (
          <p className="incoming-inspection-scanned-hint">
            {scannedItemIds.size} item(ns) localizado(s) por leitura de código
          </p>
        )}
      </div>

      <div className="incoming-inspection-grid">
        {/* Items Canvas */}
        <div className="incoming-inspection-items-canvas">
          {filteredItems.length === 0 ? (
            <div className="incoming-inspection-empty-search">
              <p>Nenhum item encontrado para &quot;{searchTerm}&quot;</p>
            </div>
          ) : (
          filteredItems.map((item) => {
            const expectedQty = Number(item.expected_qty) || 0;
            const countedQty = Number(item.counted_qty) || 0;
            const difference = countedQty - expectedQty;
            const hasDifference = Math.abs(difference) > 0.01;
            const isScanned = scannedItemIds.has(item.id);
            const isHighlighted = highlightedItemId === item.id;

            return (
              <div
                key={item.id}
                id={`inspection-item-card-${item.id}`}
                className={[
                  'incoming-inspection-item-card-modern',
                  item.is_verified ? 'verified' : '',
                  hasDifference ? 'has-difference' : '',
                  isScanned ? 'scanned' : '',
                  isHighlighted ? 'highlight-flash' : ''
                ].filter(Boolean).join(' ')}
              >
                {item.is_verified && (
                  <div className="incoming-inspection-item-verified-badge">
                    <CheckCircle2 size={32} />
                  </div>
                )}

                <div className="incoming-inspection-item-grid">
                  {/* Product Info */}
                  <div className="incoming-inspection-item-product-info">
                    <div className="incoming-inspection-item-icon">
                      <Package size={32} />
                    </div>
                    <div className="incoming-inspection-item-details">
                      <p className="incoming-inspection-item-sku-label">
                        {item.sku || `SKU-${item.product_id}`}
                      </p>
                      <h4 className="incoming-inspection-item-name-modern">
                        {item.product_name || `Produto #${item.product_id}`}
                      </h4>
                      {item.barcode && (
                        <p className="incoming-inspection-item-barcode">
                          Cód. Barras: {item.barcode}
                        </p>
                      )}
                      {item.anvisa && (
                        <p className="incoming-inspection-item-anvisa">
                          ANVISA: {item.anvisa}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Inputs Row */}
                  <div className="incoming-inspection-item-inputs">
                    <div className="incoming-inspection-inputs-row incoming-inspection-row--qty-lot">
                      <div className="incoming-inspection-input-group">
                        <label className="incoming-inspection-input-label">
                          Contagem Física
                        </label>
                        <div className="incoming-inspection-quantity-input">
                          <button
                            onClick={() => handleItemChange(item.id, 'counted_qty', Math.max(0, countedQty - 1))}
                            className="incoming-inspection-quantity-btn"
                          >
                            <Minus size={16} />
                          </button>
                          <input
                            type="number"
                            value={countedQty || ''}
                            onChange={(e) => handleItemChange(item.id, 'counted_qty', parseInt(e.target.value) || 0)}
                            className="incoming-inspection-quantity-input-field"
                            placeholder="0"
                          />
                          <button
                            onClick={() => handleItemChange(item.id, 'counted_qty', countedQty + 1)}
                            className="incoming-inspection-quantity-btn"
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="incoming-inspection-input-group">
                        <label className="incoming-inspection-input-label">
                          Número do Lote
                        </label>
                        <div className="incoming-inspection-input-with-icon">
                          <Hash size={14} className="incoming-inspection-input-icon" />
                          <input
                            type="text"
                            value={item.lot || ''}
                            onChange={(e) => handleItemChange(item.id, 'lot', e.target.value.toUpperCase())}
                            placeholder="L-0000"
                            className="incoming-inspection-input-modern"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="incoming-inspection-inputs-row incoming-inspection-row--expiry-scan">
                      <div className="incoming-inspection-input-group incoming-inspection-input-group--full">
                        <label className="incoming-inspection-input-label">
                          Validade
                        </label>
                        <div className="incoming-inspection-input-with-icon">
                          <Calendar size={14} className="incoming-inspection-input-icon" />
                          <input
                            type="text"
                            value={formatExpiryForDisplay(item.expiry)}
                            onChange={(e) => {
                              // Aceitar formato MM/AAAA
                              const value = e.target.value.replace(/\D/g, '');
                              if (value.length <= 6) {
                                let formatted = value;
                                if (value.length > 2) {
                                  formatted = value.slice(0, 2) + '/' + value.slice(2);
                                }
                                // Converter para formato de data para salvar
                                if (formatted.length === 7 && formatted.includes('/')) {
                                  const [month, year] = formatted.split('/');
                                  const dateValue = `${year}-${month.padStart(2, '0')}-01`;
                                  handleItemChange(item.id, 'expiry', dateValue);
                                } else {
                                  handleItemChange(item.id, 'expiry', formatted);
                                }
                              }
                            }}
                            placeholder="MM/AAAA"
                            className="incoming-inspection-input-modern"
                            maxLength={7}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
          )}
        </div>

        {/* Audit Sidebar */}
        <div className="incoming-inspection-audit-sidebar">
          <div className="incoming-inspection-audit-card">
            <div className="incoming-inspection-audit-glow" />
            
            <h3 className="incoming-inspection-audit-title">
              <ShieldCheck size={16} className="incoming-inspection-audit-title-icon" />
              Resumo da Auditoria
            </h3>

            <div className="incoming-inspection-audit-content">
              <div className="incoming-inspection-audit-stats-header">
                <div>
                  <p className="incoming-inspection-audit-stats-label">Itens Auditados</p>
                  <p className="incoming-inspection-audit-stats-value">
                    {items.filter(i => i.is_verified).length} <span className="incoming-inspection-audit-stats-total">/ {items.length}</span>
                  </p>
                </div>
                <div className="incoming-inspection-audit-icon-box">
                  <RefreshCw size={20} className="incoming-inspection-audit-icon-spin" />
                </div>
              </div>

              <div className="incoming-inspection-audit-compliance">
                <p className="incoming-inspection-audit-compliance-label">Riscos de Conformidade</p>
                <div className="incoming-inspection-audit-compliance-warning">
                  <AlertTriangle size={18} className="incoming-inspection-audit-compliance-icon" />
                  <p className="incoming-inspection-audit-compliance-text">
                    {items.filter(item => {
                      if (!item.expiry) return false;
                      const expiryDate = new Date(item.expiry);
                      const now = new Date();
                      const monthsDiff = (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30);
                      return monthsDiff < 12 && monthsDiff > 0;
                    }).length} lote(s) possuem validade inferior a 12 meses. Verifique antes de prosseguir.
                  </p>
                </div>
              </div>

              <div className="incoming-inspection-audit-info">
                <Info size={16} className="incoming-inspection-audit-info-icon" />
                <p className="incoming-inspection-audit-info-text">
                  A contagem cega é um requisito do Medik Quality Hub para garantir a precisão do estoque.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}

      {isMobileView && (
        <button
          type="button"
          className="incoming-inspection-scan-btn incoming-inspection-scan-btn--floating"
          onClick={() => setScannerOpen(true)}
          title="Escanear código de barras"
          aria-label="Escanear código de barras"
        >
          <Camera size={22} />
        </button>
      )}

      <MobileBarcodeScanner
        isOpen={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleBarcodeScan}
        title="Escanear produto da conferência"
      />

      {showConfirmModal && confirmModalConfig && (
        <ConfirmModal
          isOpen={showConfirmModal}
          title={confirmModalConfig.title}
          message={confirmModalConfig.message}
          type={confirmModalConfig.type}
          onConfirm={confirmModalConfig.onConfirm}
          onClose={() => {
            setShowConfirmModal(false);
            setConfirmModalConfig(null);
          }}
          confirmText="Confirmar"
          cancelText="Cancelar"
        />
      )}
    </div>
  );
};

export default IncomingInspection;
